import { constants } from "lib/utils/constants";
import { ErrorType } from "lib/types/error";
import { CfV3Payment } from "lib/types/payments";
import { triggerFuncSentryError } from "lib/utils/error";
import { loadScript } from "lib/utils/helpers";


const getMode = (): "production" | "sandbox" =>
    import.meta.env.VITE_NODE_ENV === constants.ENV_PROD ? "production" : "sandbox";
// new version url by cashfree
const CF_SDK_URL_V3 = "https://sdk.cashfree.com/js/v3/cashfree.js";


let cfInstance:any = null
export const loadV3cfSDK = async () => {
    try {
      const response = await loadScript(CF_SDK_URL_V3, () => onCfSdkLoad());
      return response;
    } catch (e) {
      console.error(e);
      throw e;
    }
};

const onCfSdkLoad = () =>
    new Promise((resolve, reject) => {
      try {
        cfInstance = (window as any).Cashfree({ mode: getMode() });
        return resolve({});
      } catch (e) {
        return reject(e);
      }
});

const CF_INLINE_CONTAINER_ID = "payment-form";

const waitForElement = async (id: string, timeoutMs = 2000): Promise<HTMLElement> => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const el = document.getElementById(id);
    if (el) return el;
    await new Promise((r) => setTimeout(r, 30));
  }
  throw new Error(`Container #${id} not found for CF V3 inline mount`);
};

export const initializeCfV3Payment = async ({
    paymentSessionId,
    paymentMode,
    onSuccessHandler,
    onFailureHandler,
    onCancelHandler,
  }: CfV3Payment) => {
    if (!cfInstance) throw new Error("Cashfree SDK not initialized");

    try {
        const containerEl = await waitForElement(CF_INLINE_CONTAINER_ID);
        const result = await cfInstance.checkout({
            paymentSessionId,
            redirectTarget: containerEl,
            appearance: { width: "100%", height: "100%" },
        });
        if (result?.paymentDetails) {
            onSuccessHandler(paymentMode, result.paymentDetails);
            return;
          }
      
          if (result?.redirect) {
            triggerFuncSentryError({
              errorType: ErrorType.CHECKOUT_ERROR,
              extraData: { paymentMode, paymentSessionId, reason: "cf_v3_modal_redirect_fallback" },
              errMessage: "CF V3 modal fell back to full-page redirect",
            });
            return;
          }
      
          if (result?.error) {
            if (isCancelError(result.error)) {
              onCancelHandler?.(paymentMode, result.error);
            } else {
              onFailureHandler(paymentMode, result.error);
            }
          }

    } catch (error) {
        console.log(error)
        onFailureHandler(paymentMode, error);
        throw error;
    }
  }

  const isCancelError = (error: any): boolean => {
    const code = error?.code?.toString().toLowerCase() ?? "";
    const msg = error?.message?.toString().toLowerCase() ?? "";
    return code.includes("payment_aborted") || msg.includes("closed") || msg.includes("cancel");
  };
  
export const initializeCfSubscription = async ({
  subsSessionId,
  redirectTarget = "_blank",
}: {
  subsSessionId: string;
  redirectTarget?: string;
}) => {
  if (!cfInstance) throw new Error("Cashfree SDK not initialized");

  const result = await cfInstance.subscriptionsCheckout({ subsSessionId, redirectTarget });

  if (result?.error) {
    triggerFuncSentryError({
      errorType: ErrorType.CHECKOUT_ERROR,
      extraData: { subsSessionId, redirectTarget, reason: "cf_subscriptions_checkout_error" },
      errMessage: result.error?.message ?? "Cashfree subscriptions checkout failed",
    });
    throw result.error;
  }

  return result;
};
